const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

if (!API_BASE_URL && !import.meta.env.DEV) {
  console.error(
    '[api] VITE_API_URL is not set. API calls will use relative paths, which is likely wrong for staging/production builds.'
  );
}

export function apiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

export function mockAuthHeaders(): Record<string, string> {
  if (import.meta.env.DEV || import.meta.env.VITE_MOCK_AUTH === 'true') {
    return {
      'X-Mock-User-Role': 'viewer',
      'X-Mock-User-Email': 'viewer@abetech.com',
    };
  }
  return {};
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = apiUrl(path);

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...mockAuthHeaders(),
        ...options.headers,
      },
      credentials: 'include',
    });
  } catch (networkError) {
    const message = networkError instanceof TypeError
      ? `Unable to reach the API at ${url}. Check your network connection.`
      : `Network error: ${networkError instanceof Error ? networkError.message : String(networkError)}`;
    throw new Error(message);
  }

  if (!response.ok) {
    const contentType = response.headers.get('Content-Type') ?? '';
    const responseText = await response.text();
    let errorMessage = `API error: ${response.status}`;

    if (contentType.includes('application/json')) {
      try {
        const error = JSON.parse(responseText);
        errorMessage = error.error?.message ?? errorMessage;
      } catch (parseErr) {
        console.warn('[apiFetch] Failed to parse JSON error response:', parseErr);
      }
    } else if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
      console.error('[apiFetch] Received HTML instead of JSON — check VITE_API_URL config', { status: response.status, url });
      errorMessage = `API returned HTML instead of JSON (status ${response.status}). The API URL may be misconfigured.`;
    } else {
      errorMessage = responseText || response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  return response.json();
}
